import React, { useState, useEffect } from 'react';
import { EventInstance, EventTemplate } from '../../../types';
import { groupEventService } from '../../../services/groupEventService';
import { toLocalDateInput } from '../../../utils/dateUtils';

interface EventInstanceFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (event: Partial<EventInstance>) => void;
  event?: EventInstance | null;
  mode: 'create' | 'edit';
}

interface FormErrors {
  booking_code?: string;
  name?: string;
  start_date?: string;
  end_date?: string;
  booking_start_date?: string;
  booking_end_date?: string;
  min_nights?: string;
  max_nights?: string;
}

const BOOKING_CODE_PATTERN = /^[A-Z0-9_-]{4,25}$/;

const EventInstanceForm: React.FC<EventInstanceFormProps> = ({
  isOpen,
  onClose,
  onSave,
  event,
  mode
}) => {
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [formData, setFormData] = useState<Partial<EventInstance>>({
    template_id: null,
    booking_code: '',
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    booking_start_date: '',
    booking_end_date: '',
    min_nights: 1,
    max_nights: undefined,
    max_sites_per_booking: 1,
    stay_buffer_days: undefined,
    default_price: undefined,
    sites_blocked: true,
    active: true
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load templates when form opens
  useEffect(() => {
    if (isOpen) {
      setLoadingTemplates(true);
      groupEventService.getTemplates(undefined, true)
        .then(data => setTemplates(data))
        .catch(err => console.error('Error loading templates:', err))
        .finally(() => setLoadingTemplates(false));
    }
  }, [isOpen]);

  // Reset form when opening or event changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && event) {
        setFormData({
          template_id: event.template_id,
          booking_code: event.booking_code,
          name: event.name,
          description: event.description || '',
          start_date: toLocalDateInput(event.start_date),
          end_date: toLocalDateInput(event.end_date),
          booking_start_date: toLocalDateInput(event.booking_start_date),
          booking_end_date: toLocalDateInput(event.booking_end_date),
          min_nights: event.min_nights,
          max_nights: event.max_nights,
          max_sites_per_booking: event.max_sites_per_booking,
          stay_buffer_days: event.stay_buffer_days,
          default_price: event.default_price,
          sites_blocked: event.sites_blocked,
          active: event.active
        });
      } else {
        // Reset to defaults for create mode
        setFormData({
          template_id: null,
          booking_code: '',
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          booking_start_date: '',
          booking_end_date: '',
          min_nights: 1,
          max_nights: undefined,
          max_sites_per_booking: 1,
          stay_buffer_days: undefined,
          default_price: undefined,
          sites_blocked: true,
          active: true
        });
      }
      setErrors({});
    }
  }, [isOpen, event, mode]);

  // Handle template selection - auto-fill fields from template
  const handleTemplateChange = (templateId: number | null) => {
    setFormData(prev => ({ ...prev, template_id: templateId }));

    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          template_id: templateId,
          booking_code: prev.booking_code || template.default_booking_code,
          min_nights: template.min_nights,
          max_nights: template.max_nights,
          max_sites_per_booking: template.max_sites_per_booking,
          stay_buffer_days: template.stay_buffer_days,
          default_price: template.default_price,
          sites_blocked: template.sites_blocked
        }));
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    let processedValue: string | number | boolean | undefined = value;

    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = value === '' ? undefined : parseFloat(value);
    } else if (name === 'booking_code') {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));

    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    // Booking code validation
    if (!formData.booking_code) {
      newErrors.booking_code = 'Booking code is required';
    } else if (!BOOKING_CODE_PATTERN.test(formData.booking_code)) {
      newErrors.booking_code = 'Code must be 4-25 characters (A-Z, 0-9, hyphen, underscore)';
    }

    // Name validation
    if (!formData.name?.trim()) {
      newErrors.name = 'Event name is required';
    }

    // Date validations
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        newErrors.end_date = 'End date must be on or after start date';
      }
    }

    // Booking window validation
    if (formData.booking_start_date && formData.booking_end_date) {
      if (new Date(formData.booking_end_date) < new Date(formData.booking_start_date)) {
        newErrors.booking_end_date = 'Booking end must be on or after booking start';
      }
    }

    // Night limits validation
    if (formData.min_nights && formData.max_nights) {
      if (formData.max_nights < formData.min_nights) {
        newErrors.max_nights = 'Max nights must be >= min nights';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean up empty optional fields
      const cleanedData: Partial<EventInstance> = {
        ...formData,
        booking_start_date: formData.booking_start_date || undefined,
        booking_end_date: formData.booking_end_date || undefined,
        description: formData.description || undefined
      };

      await onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content event-form-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === 'create' ? 'Create New Event' : 'Edit Event'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="event-form">
          <div className="form-body">
            {/* Template Selection */}
            <div className="form-group">
              <label htmlFor="template_id">Event Template (Optional)</label>
              <select
                id="template_id"
                name="template_id"
                value={formData.template_id || ''}
                onChange={(e) => handleTemplateChange(e.target.value ? parseInt(e.target.value) : null)}
                disabled={loadingTemplates}
              >
                <option value="">None (One-off Event)</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.default_booking_code})
                  </option>
                ))}
              </select>
              <span className="form-hint">
                {loadingTemplates ? 'Loading templates...' : 'Select a template to auto-fill default settings'}
              </span>
            </div>

            <div className="form-row">
              {/* Booking Code */}
              <div className="form-group">
                <label htmlFor="booking_code">Booking Code *</label>
                <input
                  type="text"
                  id="booking_code"
                  name="booking_code"
                  value={formData.booking_code || ''}
                  onChange={handleChange}
                  placeholder="e.g., SUMMER2026"
                  maxLength={25}
                  className={errors.booking_code ? 'error' : ''}
                />
                {errors.booking_code && <span className="form-error">{errors.booking_code}</span>}
              </div>

              {/* Event Name */}
              <div className="form-group">
                <label htmlFor="name">Event Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  placeholder="e.g., Summer Music Festival 2026"
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                placeholder="Optional event description..."
                rows={2}
              />
            </div>

            <div className="form-section">
              <h3>Event Dates</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="start_date">Start Date *</label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date || ''}
                    onChange={handleChange}
                    className={errors.start_date ? 'error' : ''}
                  />
                  {errors.start_date && <span className="form-error">{errors.start_date}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="end_date">End Date *</label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date || ''}
                    onChange={handleChange}
                    min={formData.start_date || ''}
                    className={errors.end_date ? 'error' : ''}
                  />
                  {errors.end_date && <span className="form-error">{errors.end_date}</span>}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Booking Window (Optional)</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="booking_start_date">Booking Opens</label>
                  <input
                    type="date"
                    id="booking_start_date"
                    name="booking_start_date"
                    value={formData.booking_start_date || ''}
                    onChange={handleChange}
                    className={errors.booking_start_date ? 'error' : ''}
                  />
                  {errors.booking_start_date && <span className="form-error">{errors.booking_start_date}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="booking_end_date">Booking Closes</label>
                  <input
                    type="date"
                    id="booking_end_date"
                    name="booking_end_date"
                    value={formData.booking_end_date || ''}
                    onChange={handleChange}
                    min={formData.booking_start_date || ''}
                    className={errors.booking_end_date ? 'error' : ''}
                  />
                  {errors.booking_end_date && <span className="form-error">{errors.booking_end_date}</span>}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Booking Rules</h3>
              <div className="form-row form-row-4">
                <div className="form-group">
                  <label htmlFor="min_nights">Min Nights</label>
                  <input
                    type="number"
                    id="min_nights"
                    name="min_nights"
                    value={formData.min_nights ?? ''}
                    onChange={handleChange}
                    min={1}
                    className={errors.min_nights ? 'error' : ''}
                  />
                  {errors.min_nights && <span className="form-error">{errors.min_nights}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="max_nights">Max Nights</label>
                  <input
                    type="number"
                    id="max_nights"
                    name="max_nights"
                    value={formData.max_nights ?? ''}
                    onChange={handleChange}
                    min={formData.min_nights || 1}
                    className={errors.max_nights ? 'error' : ''}
                  />
                  {errors.max_nights && <span className="form-error">{errors.max_nights}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="max_sites_per_booking">Max Sites/Booking</label>
                  <input
                    type="number"
                    id="max_sites_per_booking"
                    name="max_sites_per_booking"
                    value={formData.max_sites_per_booking ?? 1}
                    onChange={handleChange}
                    min={1}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="default_price">Nightly Price ($)</label>
                  <input
                    type="number"
                    id="default_price"
                    name="default_price"
                    value={formData.default_price ?? ''}
                    onChange={handleChange}
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Site & Booking Settings</h3>
              <div className="form-row">
                <div className="form-group form-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="sites_blocked"
                      checked={formData.sites_blocked ?? true}
                      onChange={handleChange}
                    />
                    Block sites for event only
                  </label>
                  <span className="form-hint">Only assigned sites visible for event booking (recommended)</span>
                </div>

                <div className="form-group">
                  <label htmlFor="stay_buffer_days">Stay Buffer (days)</label>
                  <input
                    type="number"
                    id="stay_buffer_days"
                    name="stay_buffer_days"
                    value={formData.stay_buffer_days ?? ''}
                    onChange={handleChange}
                    min={0}
                    placeholder="0"
                  />
                  <span className="form-hint">Days before/after event dates customers can book</span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Event' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventInstanceForm;
